import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiDownload, FiPrinter, FiRefreshCw } from "react-icons/fi";
import { useMsal } from "@azure/msal-react";
import { useAuthFetch } from "../hooks/useAuthFetch";
import { proposalTemplatesService } from "../services/api/proposalTemplatesService";
import {
  createDailyReport,
  deleteDailyReport,
  getDailyReport,
  listDailyReports,
  updateDailyReport,
} from "../utils/dailyReportsApi";
import { elementToImagePdfBlob, pdfFileName } from "../utils/dailyReportPdf";
import {
  belongsToReport,
  ensureReportLabel,
  fileNameForReport,
  reportFilePart,
} from "../utils/reportSaveNames";
import {
  deleteProjectItem,
  getGraphToken,
  uploadProjectFile,
} from "../utils/projectDatabaseFiles";
import {
  fillTemplateHtml,
  sourcesForKeys,
  templateFieldKeys,
} from "../utils/reportFieldCatalog";
import { loadProjectFieldContext } from "../utils/reportFieldData";

const OFFICE_LINE = "Corporate: 2850 Fairfax Trfy, Kansas City, KS 66115 | 816.561.7667";

/** Highlights empty API fields on screen only; print and PDF stay clean. */
const EMPTY_FIELD_STYLE = `<style data-nettles-field-hint>
@media screen {
  .f[data-field]:empty { display:inline-block; min-width:4ch; background:#fef9c3; box-shadow:inset 0 -1px 0 #eab308; padding:0 3px; border-radius:2px; }
  .f[data-field]:empty::after { content:attr(data-ph); color:#a16207; font-size:.8em; font-weight:400; }
}
</style>`;

function prepareHtml(html) {
  let out = String(html || "");
  out = out.split("{{LOGO_SRC}}").join("/assets/nettles_logo_color.png");
  out = out.split("{{CLIENT_LOGO}}").join("");
  out = out.split("{{OFFICE_LINE}}").join(OFFICE_LINE);
  return out;
}

function withHint(html) {
  if (!html || html.includes("data-nettles-field-hint")) return html;
  return /<\/head>/i.test(html)
    ? html.replace(/<\/head>/i, `${EMPTY_FIELD_STYLE}</head>`)
    : `${EMPTY_FIELD_STYLE}${html}`;
}

/**
 * A saved report template filled with the selected project's API values.
 * Fields the API does not return stay empty (highlighted on screen).
 */
export default function ProjectTemplateReport({ templateId, projectData, preparedBy }) {
  const { instance, accounts } = useMsal();
  const account = accounts?.[0];
  const { getAuthConfig, isReady } = useAuthFetch();
  const iframeRef = useRef(null);
  const chooserRef = useRef(null);
  const nameTouched = useRef(false);
  const reportIdRef = useRef("");
  const pdfItemIdRef = useRef("");
  const [template, setTemplate] = useState(null);
  const [templateError, setTemplateError] = useState("");
  const [result, setResult] = useState(null);
  const [dataErrors, setDataErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [savedHtml, setSavedHtml] = useState("");
  const [savedReports, setSavedReports] = useState([]);
  const [reportId, setReportId] = useState("");
  const [reportName, setReportName] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [reportQuery, setReportQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusNote, setStatusNote] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [listVersion, setListVersion] = useState(0);

  // 1) Load the saved template.
  useEffect(() => {
    let cancelled = false;
    setTemplate(null);
    setResult(null);
    setTemplateError("");
    if (!templateId) {
      setTemplateError("No report was chosen. Pick one from Nettles Links > Reports.");
      return undefined;
    }
    proposalTemplatesService
      .get(templateId)
      .then((rec) => {
        if (!cancelled) setTemplate(rec);
      })
      .catch((error) => {
        if (cancelled) return;
        const status = error?.response?.status;
        setTemplateError(
          status === 403
            ? "You do not have access to this saved report."
            : error?.response?.data?.message || error?.message || "Could not load this report."
        );
      });
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const reportLabel = template?.name || "Report";
  const projectCode = String(projectData?.projectId || projectData?.projectNo || "").trim();

  useEffect(() => {
    reportIdRef.current = "";
    pdfItemIdRef.current = "";
    nameTouched.current = false;
    setReportId("");
    setSavedHtml("");
    setPdfUrl("");
    setReportName("");
    setStatusNote("");
  }, [templateId, projectCode]);

  // 2) Load only the API sources the template needs, then fill it.
  const html = template?.html || "";
  const keys = useMemo(() => templateFieldKeys(html), [html]);

  useEffect(() => {
    if (!html || !isReady) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const ctx = await loadProjectFieldContext({
          opportunityId: projectData?.id_project,
          selected: projectData || {},
          getAuthConfig,
          sources: sourcesForKeys(keys),
          preparedBy,
        });
        if (cancelled) return;
        const filled = fillTemplateHtml(prepareHtml(html), ctx);
        setDataErrors(ctx.errors || {});
        setResult({ ...filled, html: withHint(filled.html) });
      } catch (error) {
        if (cancelled) return;
        console.error("Report data failed to load:", error);
        // Still show the template, with every field empty.
        const filled = fillTemplateHtml(prepareHtml(html), { raw: {}, selected: projectData || {}, meta: { preparedBy } });
        setDataErrors({ project: error?.message || "Could not load project data" });
        setResult({ ...filled, html: withHint(filled.html) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // projectData identity changes often; the ids below are what matter for a reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, keys, isReady, projectData?.id_project, reloadTick]);

  useEffect(() => {
    if (!projectCode || !isReady || !reportLabel) return undefined;
    let cancelled = false;
    listDailyReports(getAuthConfig, projectCode)
      .then((rows) => {
        if (!cancelled) setSavedReports(rows.filter((report) => belongsToReport(report.name, reportLabel)));
      })
      .catch((error) => {
        if (!cancelled) setStatusNote(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCode, isReady, getAuthConfig, listVersion, reportLabel]);

  useEffect(() => {
    if (reportIdRef.current || nameTouched.current || !reportLabel) return;
    const next = fileNameForReport(reportLabel, new Date().toISOString().slice(0, 10), savedReports.map((report) => report.name));
    setReportName((current) => (current === next ? current : next));
  }, [savedReports, reportLabel]);

  useEffect(() => {
    if (!chooserOpen) return undefined;
    const onPointerDown = (event) => {
      if (!chooserRef.current?.contains(event.target)) setChooserOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [chooserOpen]);

  const previewHtml = savedHtml || result?.html || "";

  const startNewFile = () => {
    reportIdRef.current = "";
    pdfItemIdRef.current = "";
    nameTouched.current = false;
    setReportId("");
    setSavedHtml("");
    setPdfUrl("");
    setReportName(fileNameForReport(reportLabel, new Date().toISOString().slice(0, 10), savedReports.map((report) => report.name)));
    setStatusNote("");
    setReloadTick((n) => n + 1);
    setChooserOpen(false);
  };

  const openSavedReport = (id) => {
    setChooserOpen(false);
    setStatusNote("Opening…");
    getDailyReport(getAuthConfig, id)
      .then((report) => {
        const stored = report?.form?.html || "";
        if (!stored) {
          setStatusNote("That saved file has no report content.");
          return;
        }
        reportIdRef.current = report.id;
        pdfItemIdRef.current = report.pdfItemId || "";
        nameTouched.current = true;
        setReportId(report.id);
        setReportName(report.name || "");
        setSavedHtml(stored);
        setPdfUrl(report.pdfWebUrl || "");
        setStatusNote("Opened saved file");
      })
      .catch((error) => setStatusNote(error.message));
  };

  const saveReport = async () => {
    const name = ensureReportLabel(reportName.trim(), reportLabel);
    if (!name) {
      setStatusNote("Enter a report file name before saving.");
      return;
    }
    if (!projectCode) {
      setStatusNote("Select a project before saving.");
      return;
    }
    if (name !== reportName) setReportName(name);
    const doc = iframeRef.current?.contentDocument;
    const htmlToSave = doc ? `<!DOCTYPE html>\n${doc.documentElement.outerHTML}` : previewHtml;
    if (!htmlToSave) {
      setStatusNote("The report is still loading.");
      return;
    }
    setSaving(true);
    setStatusNote("Saving…");
    const payload = {
      projectCode,
      opportunityId: projectData?.id_project || null,
      name,
      reportDate: new Date().toISOString().slice(0, 10),
      form: { html: htmlToSave, templateId: templateId || null, templateName: reportLabel },
      samples: [],
      signature: null,
    };
    try {
      let id = reportIdRef.current;
      if (!id) {
        const created = await createDailyReport(getAuthConfig, payload);
        id = created.id;
        reportIdRef.current = id;
        setReportId(id);
      } else {
        await updateDailyReport(getAuthConfig, id, payload);
      }

      const token = await getGraphToken(instance, account);
      let pdfItemId = pdfItemIdRef.current || "";
      let pdfWebUrl = "";
      let pdfName = "";
      try {
        const page = doc?.querySelector(".page") || doc?.body;
        if (page) {
          const pdfBlob = await elementToImagePdfBlob(page);
          pdfName = pdfFileName(name, id);
          const previousPdfId = pdfItemId;
          const uploadedPdf = await uploadProjectFile(token, {
            library: "Reports",
            segments: [projectCode, "Daily Reports"],
            fileName: pdfName,
            blob: pdfBlob,
          });
          pdfItemId = uploadedPdf.itemId;
          pdfWebUrl = uploadedPdf.webUrl;
          if (previousPdfId && previousPdfId !== pdfItemId) {
            try {
              await deleteProjectItem(token, "Reports", previousPdfId);
            } catch {
              /* the new PDF is already in SharePoint */
            }
          }
        }
      } catch (error) {
        setSavedHtml(htmlToSave);
        setStatusNote(`Saved, but the PDF could not be uploaded. Save again to retry. ${error.message || ""}`.trim());
        setListVersion((current) => current + 1);
        setSaving(false);
        return;
      }
      const saved = await updateDailyReport(getAuthConfig, id, {
        pdf: pdfItemId ? { fileName: pdfName, itemId: pdfItemId, webUrl: pdfWebUrl } : undefined,
      });
      pdfItemIdRef.current = saved.pdfItemId || pdfItemId || "";
      setPdfUrl(saved.pdfWebUrl || pdfWebUrl || "");
      setSavedHtml(htmlToSave);
      setStatusNote("Saved");
      setListVersion((current) => current + 1);
    } catch (error) {
      setStatusNote(error.message || "Couldn't save the report");
    } finally {
      setSaving(false);
    }
  };

  const deleteSavedReport = async (report) => {
    if (!window.confirm(`Delete saved file "${report?.name || "this report"}"?`)) return;
    setStatusNote("Deleting…");
    try {
      await deleteDailyReport(getAuthConfig, report.id);
      if (report.pdfItemId) {
        try {
          const token = await getGraphToken(instance, account);
          await deleteProjectItem(token, "Reports", report.pdfItemId);
        } catch {
          /* the record is already gone */
        }
      }
      if (report.id === reportIdRef.current) startNewFile();
      setListVersion((current) => current + 1);
      setStatusNote("Deleted");
      setChooserOpen(false);
    } catch (error) {
      setStatusNote(error.message);
    }
  };

  const printReport = useCallback(() => {
    const frame = iframeRef.current?.contentWindow;
    if (!frame) return;
    frame.focus();
    frame.print();
  }, []);

  const downloadHtml = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    const source = doc ? `<!DOCTYPE html>\n${doc.documentElement.outerHTML}` : result?.html || "";
    if (!source) return;
    const blob = new Blob([source], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safe = `${template?.name || "report"} - ${projectData?.projectName || "project"}`.replace(/[^\w.-]+/g, "_");
    link.href = url;
    link.download = `${safe}.html`;
    link.click();
    URL.revokeObjectURL(url);
  }, [result, template, projectData?.projectName]);

  const failedSources = Object.keys(dataErrors);
  const reportSearch = reportQuery.trim().toLowerCase();
  const visibleReports = savedReports.filter((report) => (
    !reportSearch || String(report.name || "").toLowerCase().includes(reportSearch)
  ));
  const inputClass = "mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-800 outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 bg-stone-50 px-4 py-4 pb-8 text-stone-800 lg:px-6 min-[1025px]:pt-9">
      <div className="flex flex-col gap-3 rounded-xl border border-stone-200 bg-white px-4 py-3 shadow-sm">
        <div className="min-w-0">
          <h2 className="truncate text-base font-bold text-stone-900">{reportLabel}</h2>
          <p className="truncate text-xs text-stone-500">
            {projectData?.projectName || "Selected project"}
            {projectCode ? ` · ${projectCode}` : ""}
            {statusNote ? ` · ${statusNote}` : ""}
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            Report file name
            <div className="mt-1 flex min-w-0 items-center overflow-hidden rounded-lg border border-stone-200 bg-white focus-within:ring-1 focus-within:ring-blue-500">
              <span className="shrink-0 border-r border-stone-200 bg-stone-50 px-3 py-2 text-sm text-stone-500" title="Kept on every save so this report lists its own files">
                {reportLabel} ::
              </span>
              <input
                className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-stone-800 outline-none"
                value={reportFilePart(reportName, reportLabel)}
                maxLength={120}
                placeholder="File name"
                onChange={(event) => {
                  nameTouched.current = true;
                  setReportName(ensureReportLabel(event.target.value, reportLabel));
                }}
              />
            </div>
          </label>
          <div ref={chooserRef} className="relative">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Saved reports</span>
            <button
              type="button"
              onClick={() => {
                setReportQuery("");
                setChooserOpen((open) => !open);
              }}
              className={`${inputClass} flex items-center justify-between gap-2 text-left font-normal`}
            >
              <span className={`min-w-0 flex-1 truncate ${reportId ? "text-stone-800" : "text-stone-400"}`}>
                {reportId ? (savedReports.find((report) => report.id === reportId)?.name || reportName) : "Select a saved file"}
              </span>
              <span className="text-stone-400">{chooserOpen ? "▴" : "▾"}</span>
            </button>
            {chooserOpen ? (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-lg border border-stone-200 bg-white shadow-lg">
                <div className="border-b border-stone-100 p-2">
                  <input
                    value={reportQuery}
                    onChange={(event) => setReportQuery(event.target.value)}
                    placeholder="Search by report file name"
                    className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto py-1">
                  {savedReports.length === 0 ? <p className="px-3 py-3 text-sm text-stone-500">No saved files for this report yet.</p> : null}
                  {savedReports.length > 0 && visibleReports.length === 0 ? (
                    <p className="px-3 py-3 text-sm text-stone-500">No files match “{reportQuery.trim()}”.</p>
                  ) : null}
                  {visibleReports.map((report) => (
                    <div key={report.id} className={`flex items-center gap-2 px-2 py-1.5 ${report.id === reportId ? "bg-red-50" : "hover:bg-stone-50"}`}>
                      <button type="button" onClick={() => openSavedReport(report.id)} className="min-w-0 flex-1 rounded-md px-1 py-1 text-left">
                        <div className="truncate text-sm font-semibold text-stone-800">{report.name}</div>
                      </button>
                      {report.pdfWebUrl ? (
                        <a href={report.pdfWebUrl} target="_blank" rel="noreferrer" className="shrink-0 rounded px-1.5 py-1 text-xs font-bold text-red-600 hover:bg-red-50">PDF</a>
                      ) : null}
                      <button type="button" onClick={() => deleteSavedReport(report)} className="shrink-0 rounded px-1.5 py-1 text-xs font-bold text-red-600 hover:bg-red-50">Delete</button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={startNewFile} className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-semibold hover:bg-stone-100">
              New
            </button>
            <button type="button" onClick={saveReport} disabled={saving || !previewHtml} className="rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {saving ? "Saving…" : reportId ? "Update" : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setSavedHtml("");
                setReloadTick((n) => n + 1);
              }}
              disabled={loading || !template}
              className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-semibold hover:bg-stone-100 disabled:opacity-50"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} /> Reload data
            </button>
            <button type="button" onClick={downloadHtml} disabled={!previewHtml} className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-semibold hover:bg-stone-100 disabled:opacity-50">
              <FiDownload /> Download
            </button>
            <button type="button" onClick={printReport} disabled={!previewHtml} className="inline-flex items-center gap-1 rounded-lg border border-stone-300 bg-white px-3 py-2 text-xs font-semibold hover:bg-stone-100 disabled:opacity-50">
              <FiPrinter /> Print
            </button>
            {pdfUrl ? (
              <a href={pdfUrl} target="_blank" rel="noreferrer" className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700">
                Open PDF
              </a>
            ) : null}
          </div>
        </div>
      </div>

      {templateError && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <FiAlertTriangle className="shrink-0" /> {templateError}
        </div>
      )}

      {!templateError && !previewHtml && (
        <div className="text-sm text-stone-500">
          {template ? "Loading this project's data into the report..." : "Loading report..."}
        </div>
      )}

      {result && !savedHtml && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-stone-200 bg-white px-3 py-2 text-xs text-stone-600">
          <span>
            <strong className="text-emerald-700">{result.filled}</strong> of {result.total} fields filled from the API
          </span>
          {result.total === 0 && (
            <span className="text-amber-700">
              This template has no API fields yet. Add some under Configuration &gt; Reports &amp; Proposal Templates &gt; API fields.
            </span>
          )}
          {result.missing.length > 0 && (
            <details className="min-w-0">
              <summary className="cursor-pointer text-amber-700">
                {result.missing.length} not available in the API (left empty)
              </summary>
              <ul className="mt-1 list-disc pl-5 text-stone-500">
                {result.missing.map((m, i) => (
                  <li key={`${m.key}-${i}`}>
                    {m.label} <span className="font-mono text-[10px] text-stone-400">{m.key}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {failedSources.length > 0 && (
            <span className="text-amber-700">
              Some data sources did not respond ({failedSources.join(", ")}). Reload to try again.
            </span>
          )}
        </div>
      )}

      {previewHtml && (
        <iframe
          ref={iframeRef}
          title={reportLabel}
          srcDoc={withHint(previewHtml)}
          className="w-full flex-1 rounded-xl border border-stone-200 bg-white"
          style={{ minHeight: "calc(100vh - 16rem)" }}
        />
      )}
    </div>
  );
}
